/**
 * DataTable — Generic sortable, filterable, paginated table with inline editing.
 * Handles 2000+ rows efficiently via pagination (no virtual scroll dependency).
 *
 * Props:
 *   columns: [{ key, label, width?, editable?, render? }]
 *   data: array of objects
 *   pageSize: number (default 50)
 *   searchable: bool (default true)
 *   onEdit: (rowIndex, key, value) => void (optional — enables inline editing)
 *   rowClassName: (row, index) => string (optional — per-row class)
 *   extraColumns: [{ key, label, render }] — appended to the right (e.g. lock icon)
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import '../styles/DataTable.css';

export default function DataTable({
  columns,
  data,
  pageSize: defaultPageSize = 50,
  searchable = true,
  onEdit,
  rowClassName,
  extraColumns = [],
  selectable = false,
  onBulkEdit,
  columnFilters = [], // [{ key, label, options: [{value, label}], filterFn? }]
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [editCell, setEditCell] = useState(null); // { row, col }
  const [editValue, setEditValue] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [activeFilters, setActiveFilters] = useState({}); // { filterKey: selectedValue }
  const debounceRef = useRef(null);

  // Debounce search input (150ms)
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 150);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const allColumns = useMemo(() => [...columns, ...extraColumns], [columns, extraColumns]);

  // Reset selection when data changes
  useEffect(() => { setSelected(new Set()); }, [data]);

  // Filter (text search + column filters)
  const filtered = useMemo(() => {
    let result = data;
    // Apply column filters
    for (const cf of columnFilters) {
      const val = activeFilters[cf.key];
      if (!val) continue;
      if (cf.filterFn) {
        result = result.filter(row => cf.filterFn(row, val));
      } else {
        result = result.filter(row => {
          const cellVal = String(row[cf.key] || '').toLowerCase();
          return cellVal === val.toLowerCase();
        });
      }
    }
    // Apply text search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(row =>
        columns.some(col => {
          const val = row[col.key];
          return val != null && String(val).toLowerCase().includes(q);
        })
      );
    }
    return result;
  }, [data, search, columns, columnFilters, activeFilters]);

  // Sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      const na = Number(va);
      const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageData = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Inline edit
  const startEdit = useCallback((rowIdx, colKey, currentValue) => {
    if (!onEdit) return;
    const col = columns.find(c => c.key === colKey);
    if (!col?.editable) return;
    setEditCell({ row: rowIdx, col: colKey });
    setEditValue(currentValue ?? '');
  }, [onEdit, columns]);

  const commitEdit = useCallback(() => {
    if (editCell && onEdit) {
      // Find the original data index (accounting for sort/filter)
      const row = pageData[editCell.row];
      const originalIndex = data.indexOf(row);
      if (originalIndex >= 0) {
        onEdit(originalIndex, editCell.col, editValue);
      }
    }
    setEditCell(null);
  }, [editCell, editValue, onEdit, pageData, data]);

  const cancelEdit = useCallback(() => {
    setEditCell(null);
  }, []);

  // Selection helpers for bulk editing
  const toggleSelect = useCallback((originalIndex) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(originalIndex)) next.delete(originalIndex);
      else next.add(originalIndex);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const visibleIndices = filtered.map(row => data.indexOf(row));
    const allSelected = visibleIndices.length > 0 && visibleIndices.every(i => selected.has(i));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleIndices));
    }
  }, [filtered, data, selected]);

  const handleBulkApply = useCallback(() => {
    if (!bulkField || !onBulkEdit || selected.size === 0) return;
    onBulkEdit(Array.from(selected), bulkField, bulkValue);
    setSelected(new Set());
    setBulkValue('');
  }, [selected, bulkField, bulkValue, onBulkEdit]);

  const editableColumns = useMemo(() => columns.filter(c => c.editable), [columns]);

  return (
    <div className="ro-dt-container">
      {/* Bulk edit bar */}
      {selectable && selected.size > 0 && (
        <div className="ro-dt-bulk-bar">
          <span className="ro-dt-bulk-count">{selected.size} selected</span>
          <select
            value={bulkField}
            onChange={e => setBulkField(e.target.value)}
            className="ro-dt-bulk-field"
            aria-label="Field to bulk edit"
          >
            <option value="">Set field...</option>
            {editableColumns.map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={bulkValue}
            onChange={e => setBulkValue(e.target.value)}
            placeholder="New value"
            className="ro-input ro-dt-bulk-value"
            disabled={!bulkField}
          />
          <button
            className="ro-btn ro-btn-primary ro-btn-sm"
            onClick={handleBulkApply}
            disabled={!bulkField}
          >
            Apply to {selected.size}
          </button>
          <button
            className="ro-btn ro-btn-sm"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Toolbar: search + page size + count */}
      <div className="ro-dt-toolbar">
        {searchable && (
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={handleSearchChange}
            className="ro-input ro-dt-search"
            aria-label="Search table rows"
          />
        )}
        {columnFilters.map(cf => (
          <select
            key={cf.key}
            className="ro-column-filter"
            value={activeFilters[cf.key] || ''}
            onChange={e => {
              setActiveFilters(prev => ({ ...prev, [cf.key]: e.target.value }));
              setPage(0);
            }}
            aria-label={`Filter by ${cf.label}`}
          >
            <option value="">{cf.label}: All</option>
            {cf.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ))}
        <span className="ro-dt-count">
          {sorted.length === data.length
            ? `${data.length} rows`
            : `${sorted.length} of ${data.length} rows`}
        </span>
        <select
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
          className="ro-dt-page-size"
          aria-label="Results per page"
        >
          <option value={25}>25/page</option>
          <option value={50}>50/page</option>
          <option value={100}>100/page</option>
        </select>
      </div>

      {/* Table */}
      <div className="ro-dt-scroll">
        <table className="ro-dt-table">
          <thead>
            <tr>
              {selectable && (
                <th className="ro-dt-th-check">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every(row => selected.has(data.indexOf(row)))}
                    onChange={toggleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              <th className="ro-dt-th-num">#</th>
              {allColumns.map(col => {
                const isSortable = !col.render;
                const isSorted = sortKey === col.key;
                return (
                <th
                  key={col.key}
                  className={`ro-dt-th ${isSorted ? 'ro-dt-th-sorted' : ''}${!isSortable ? ' ro-dt-th-nosort' : ''}`}
                  onClick={() => isSortable && handleSort(col.key)}
                  onKeyDown={e => { if (isSortable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleSort(col.key); } }}
                  tabIndex={isSortable ? 0 : undefined}
                  role={isSortable ? 'button' : undefined}
                  aria-sort={isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  aria-label={isSortable ? `Sort by ${col.label}` : undefined}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className="ro-dt-sort-arrow">{sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}</span>
                  )}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, ri) => {
              const globalIndex = safePage * pageSize + ri + 1;
              const originalIndex = data.indexOf(row);
              const extraClass = rowClassName ? rowClassName(row, originalIndex) : '';
              const isSelected = selectable && selected.has(originalIndex);
              return (
                <tr key={ri} className={`${extraClass}${isSelected ? ' ro-dt-row-selected' : ''}`}>
                  {selectable && (
                    <td className="ro-dt-check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(originalIndex)}
                        aria-label={`Select row ${globalIndex}`}
                      />
                    </td>
                  )}
                  <td className="ro-dt-num">{globalIndex}</td>
                  {allColumns.map(col => {
                    // Custom render column (e.g., lock icon)
                    if (col.render) {
                      return <td key={col.key}>{col.render(row, originalIndex)}</td>;
                    }

                    const isEditing = editCell && editCell.row === ri && editCell.col === col.key;
                    const val = row[col.key];

                    if (isEditing) {
                      return (
                        <td key={col.key} className="ro-dt-editing">
                          <input
                            autoFocus
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            className="ro-dt-edit-input"
                          />
                        </td>
                      );
                    }

                    const displayVal = val ?? '—';
                    const titleText = col.editable && onEdit
                      ? `${displayVal} — double-click to edit`
                      : (typeof displayVal === 'string' && displayVal.length > 20 ? displayVal : undefined);
                    return (
                      <td
                        key={col.key}
                        className={col.editable && onEdit ? 'ro-dt-editable' : ''}
                        onDoubleClick={() => startEdit(ri, col.key, val)}
                        title={titleText}
                      >
                        {displayVal}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state when search/filter yields no results */}
      {pageData.length === 0 && data.length > 0 && (
        <div className="ro-dt-empty">
          No rows match &ldquo;{search}&rdquo;
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ro-dt-pagination">
          <button
            className="ro-btn ro-btn-sm"
            onClick={() => setPage(0)}
            disabled={safePage === 0}
            aria-label="First page"
          >
            &laquo;
          </button>
          <button
            className="ro-btn ro-btn-sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={safePage === 0}
            aria-label="Previous page"
          >
            &lsaquo;
          </button>
          <span className="ro-dt-page-info" aria-live="polite">
            Page {safePage + 1} of {totalPages}
          </span>
          <button
            className="ro-btn ro-btn-sm"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
            aria-label="Next page"
          >
            &rsaquo;
          </button>
          <button
            className="ro-btn ro-btn-sm"
            onClick={() => setPage(totalPages - 1)}
            disabled={safePage >= totalPages - 1}
            aria-label="Last page"
          >
            &raquo;
          </button>
        </div>
      )}
    </div>
  );
}
